import { createSlice } from "@reduxjs/toolkit";

/**
 * windowsSlice — the headless window-manager engine for the Webtop redesign.
 *
 * This is a NEW, isolated engine. It does NOT replace or touch the existing
 * `tabSlice` — the current tab system keeps working untouched (Rule 4). The
 * Webtop shell consumes this slice; the legacy shell continues to use tabs.
 *
 * A "window" is a richer version of a tab: it carries geometry (x/y/w/h),
 * stacking (z), and a lifecycle state (normal | minimized | maximized) on top
 * of the tab concept of {id, path, title}.
 *
 * Phase 1 is intentionally headless: no DOM, no localStorage, no browser
 * globals — so it is pure and unit-testable. Per-user persistence of the
 * desktop arrives in Phase 7 (DesktopLayout); window-restore is layered on
 * later without changing this contract.
 *
 * See WEBTOP_SPEC.md §5 for the window state shape.
 */

// --- Geometry constants (logical px; the chrome layer maps these to the DOM) ---
export const DEFAULT_WIDTH = 960;
export const DEFAULT_HEIGHT = 640;
export const MIN_WIDTH = 360;
export const MIN_HEIGHT = 240;

// New windows cascade so they don't stack exactly on top of each other.
const CASCADE_ORIGIN_X = 80;
const CASCADE_ORIGIN_Y = 60;
const CASCADE_STEP = 32;
const CASCADE_WRAP = 8; // restart the cascade after this many steps

// Base z-index for the lowest window. The whole window stack is kept in a small
// bounded band [BASE_Z, BASE_Z + window count) by re-packing on every focus, so
// it can never climb above the RingCentral widget (~999) no matter how many
// times windows are focused over a long session (Rule 7 — never raise RC's
// z-index; keep windows below it). See WEBTOP_SPEC §12.
const BASE_Z = 10;

export const WINDOW_STATE = {
  NORMAL: "normal",
  MINIMIZED: "minimized",
  MAXIMIZED: "maximized",
};

// Derive a readable title from a route path, e.g. "/sales/123" -> "Sales".
// Mirrors tabSlice.titleFromPath so windows and tabs label apps identically.
const titleFromPath = (path) => {
  const clean = String(path || "").replace(/^\//, "").split("/")[0];
  const spaced = clean.replace(/([A-Z])/g, " $1").trim() || "App";
  // Capitalize the first letter so a bare route like "clients" -> "Clients".
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Stable-ish unique id for a window.
const makeId = () => `win-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const normalizePath = (path) => {
  const p = String(path || "");
  return p.startsWith("/") ? p : `/${p}`;
};

// Cascade position for the Nth concurrently-open window.
const cascadePosition = (openCount) => {
  const step = openCount % CASCADE_WRAP;
  return {
    x: CASCADE_ORIGIN_X + step * CASCADE_STEP,
    y: CASCADE_ORIGIN_Y + step * CASCADE_STEP,
  };
};

const initialState = {
  windows: [],
  // id of the window that currently has focus ("" when none).
  focusedId: "",
  // monotonically increasing counter; the focused window always owns topZ.
  topZ: BASE_Z,
};

// Raise a window to the top of the stack and mark it focused. Mutates draft.
// Only updates THIS window's z (avoids touching every other window object so
// React.memo on Window can skip sibling re-renders). Re-packs the full band
// only when topZ is about to exceed the RingCentral widget ceiling (Rule 7).
const bringToFront = (state, win) => {
  state.topZ += 1;
  if (state.topZ >= 990) {
    // Full re-pack — keeps the whole stack well below RC widget z-index (~999).
    const ordered = [...state.windows].sort((a, b) => a.z - b.z);
    ordered.forEach((w, i) => { w.z = BASE_Z + i; });
    state.topZ = BASE_Z + Math.max(0, ordered.length - 1);
  }
  win.z = state.topZ;
  state.focusedId = win.id;
};

// After a window is removed/minimized, focus the top-most remaining *visible*
// (non-minimized) window, or "" if none remain.
const focusTopVisible = (state) => {
  const visible = state.windows.filter((w) => w.state !== WINDOW_STATE.MINIMIZED);
  if (visible.length === 0) {
    state.focusedId = "";
    return;
  }
  const top = visible.reduce((a, b) => (b.z > a.z ? b : a));
  state.focusedId = top.id;
};

const windowsSlice = createSlice({
  name: "windows",
  initialState,
  reducers: {
    /**
     * Open an app in a new window (or focus/restore an existing one when
     * `deduplicate` is set and a window for the same path already exists).
     * payload: { path, title?, appKey?, x?, y?, w?, h?, deduplicate? }
     */
    openWindow(state, action) {
      const { path, title, appKey, x, y, w, h, deduplicate } = action.payload || {};
      const normalizedPath = normalizePath(path);

      if (deduplicate) {
        const existing = state.windows.find((win) => win.path === normalizedPath);
        if (existing) {
          // Restore if minimized, then bring to front (back to maximized if it
          // had a maximized geometry, else normal — matches focusWindow).
          if (existing.state === WINDOW_STATE.MINIMIZED) {
            existing.state = existing.prevBounds ? WINDOW_STATE.MAXIMIZED : WINDOW_STATE.NORMAL;
          }
          bringToFront(state, existing);
          return;
        }
      }

      const pos = cascadePosition(state.windows.length);
      // The "normal" (restored) geometry. Windows OPEN MAXIMIZED by default so
      // apps fill the screen; prevBounds carries this size so the restore/double-
      // click-titlebar returns to a sensible window instead of nothing.
      const nx = Number.isFinite(x) ? x : pos.x;
      const ny = Number.isFinite(y) ? y : pos.y;
      const nw = Number.isFinite(w) ? Math.max(w, MIN_WIDTH) : DEFAULT_WIDTH;
      const nh = Number.isFinite(h) ? Math.max(h, MIN_HEIGHT) : DEFAULT_HEIGHT;
      const win = {
        id: makeId(),
        appKey: appKey || normalizedPath.replace(/^\//, "").split("/")[0] || "app",
        path: normalizedPath,
        title: title || titleFromPath(normalizedPath),
        x: nx,
        y: ny,
        w: nw,
        h: nh,
        z: BASE_Z,
        state: WINDOW_STATE.MAXIMIZED,
        prevBounds: { x: nx, y: ny, w: nw, h: nh },
      };
      state.windows.push(win);
      bringToFront(state, win);
    },

    closeWindow(state, action) {
      const id = action.payload;
      const index = state.windows.findIndex((win) => win.id === id);
      if (index === -1) return;
      state.windows.splice(index, 1);
      if (state.focusedId === id) focusTopVisible(state);
    },

    focusWindow(state, action) {
      const win = state.windows.find((w) => w.id === action.payload);
      if (!win) return;
      // Focusing a minimized window restores it.
      if (win.state === WINDOW_STATE.MINIMIZED) {
        win.state = win.prevBounds ? WINDOW_STATE.MAXIMIZED : WINDOW_STATE.NORMAL;
      }
      bringToFront(state, win);
    },

    minimizeWindow(state, action) {
      const win = state.windows.find((w) => w.id === action.payload);
      if (!win || win.state === WINDOW_STATE.MINIMIZED) return;
      win.state = WINDOW_STATE.MINIMIZED;
      if (state.focusedId === win.id) focusTopVisible(state);
    },

    maximizeWindow(state, action) {
      const win = state.windows.find((w) => w.id === action.payload);
      if (!win || win.state === WINDOW_STATE.MAXIMIZED) return;
      // Remember the normal bounds so restore returns exactly here.
      win.prevBounds = { x: win.x, y: win.y, w: win.w, h: win.h };
      win.state = WINDOW_STATE.MAXIMIZED;
      bringToFront(state, win);
    },

    restoreWindow(state, action) {
      const win = state.windows.find((w) => w.id === action.payload);
      if (!win) return;
      if (win.prevBounds) {
        win.x = win.prevBounds.x;
        win.y = win.prevBounds.y;
        win.w = win.prevBounds.w;
        win.h = win.prevBounds.h;
        win.prevBounds = null;
      }
      win.state = WINDOW_STATE.NORMAL;
      bringToFront(state, win);
    },

    // Toggle between maximized and normal — used by the title-bar maximize button
    // and double-click on the title bar.
    toggleMaximize(state, action) {
      const win = state.windows.find((w) => w.id === action.payload);
      if (!win) return;
      if (win.state === WINDOW_STATE.MAXIMIZED) {
        if (win.prevBounds) {
          win.x = win.prevBounds.x;
          win.y = win.prevBounds.y;
          win.w = win.prevBounds.w;
          win.h = win.prevBounds.h;
          win.prevBounds = null;
        }
        win.state = WINDOW_STATE.NORMAL;
      } else {
        win.prevBounds = { x: win.x, y: win.y, w: win.w, h: win.h };
        win.state = WINDOW_STATE.MAXIMIZED;
      }
      bringToFront(state, win);
    },

    moveWindow(state, action) {
      const { id, x, y } = action.payload || {};
      const win = state.windows.find((w) => w.id === id);
      if (!win) return;
      if (Number.isFinite(x)) win.x = x;
      if (Number.isFinite(y)) win.y = y;
    },

    resizeWindow(state, action) {
      const { id, w, h } = action.payload || {};
      const win = state.windows.find((wn) => wn.id === id);
      if (!win) return;
      if (Number.isFinite(w)) win.w = Math.max(w, MIN_WIDTH);
      if (Number.isFinite(h)) win.h = Math.max(h, MIN_HEIGHT);
    },

    // Set absolute geometry in one shot — used by snapping (Phase 12) and the
    // resize handles. Clamps to minimums.
    setWindowBounds(state, action) {
      const { id, x, y, w, h } = action.payload || {};
      const win = state.windows.find((wn) => wn.id === id);
      if (!win) return;
      if (Number.isFinite(x)) win.x = x;
      if (Number.isFinite(y)) win.y = y;
      if (Number.isFinite(w)) win.w = Math.max(w, MIN_WIDTH);
      if (Number.isFinite(h)) win.h = Math.max(h, MIN_HEIGHT);
    },

    setWindowTitle(state, action) {
      const { id, title } = action.payload || {};
      const win = state.windows.find((w) => w.id === id);
      if (win && title) win.title = title;
    },

    // Race-safe title update from a detail page once the record name loads,
    // targeting by path (the window may not be focused when the fetch resolves).
    // Mirrors tabSlice.updateTabTitleByPath.
    updateWindowTitleByPath(state, action) {
      const { path, title } = action.payload || {};
      const normalizedPath = normalizePath(path);
      const win = state.windows.find((w) => w.path === normalizedPath);
      if (win && title) win.title = title;
    },

    // Update a window's stored path when in-window navigation happens
    // (the chrome's per-window NavigationTracker calls this).
    updateWindowPath(state, action) {
      const { id, path } = action.payload || {};
      const win = state.windows.find((w) => w.id === id);
      if (win) win.path = normalizePath(path);
    },

    minimizeAll(state) {
      state.windows.forEach((win) => {
        win.state = WINDOW_STATE.MINIMIZED;
      });
      state.focusedId = "";
    },

    closeAllWindows(state) {
      state.windows = [];
      state.focusedId = "";
    },

    clearWindowsOnLogout(state) {
      state.windows = [];
      state.focusedId = "";
      state.topZ = BASE_Z;
    },
  },
});

export const {
  openWindow,
  closeWindow,
  focusWindow,
  minimizeWindow,
  maximizeWindow,
  restoreWindow,
  toggleMaximize,
  moveWindow,
  resizeWindow,
  setWindowBounds,
  setWindowTitle,
  updateWindowTitleByPath,
  updateWindowPath,
  minimizeAll,
  closeAllWindows,
  clearWindowsOnLogout,
} = windowsSlice.actions;

// --- Selectors ---
export const selectWindows = (state) => state.windows.windows;
export const selectFocusedId = (state) => state.windows.focusedId;
export const selectFocusedWindow = (state) =>
  state.windows.windows.find((w) => w.id === state.windows.focusedId) || null;
// Taskbar order is stable (insertion order); visibility/min state is per-window.
export const selectTaskbarWindows = (state) => state.windows.windows;

export default windowsSlice.reducer;
